/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { create } from "zustand";
import { sopLogger } from './sop-logger';

export interface SOPStep {
  name: string;
  status: string;
  evidence: string;
  message?: string;
}

export interface SOPResponse {
  steps: SOPStep[];
}

interface SOPChecklistState {
  checklist: SOPStep[];
  isValidResponse: boolean;
  lastResponse: string;
  isInitialized: boolean;
  currentLogId: string | null;
  initializeSOP: () => void;
  addSteps: (steps: SOPStep[]) => void;
  updateStep: (index: number, step: SOPStep) => void;
  updateStepsFromAnalysis: (steps: SOPStep[]) => void;
  clearChecklist: () => void;
  setLastResponse: (response: string) => void;
  setValidResponse: (isValid: boolean) => void;
  startSOPLogging: (technicianId: string, technicianEmail: string, sopName: string) => Promise<string>;
  logStep: (step: SOPStep) => Promise<void>;
  completeSOPLogging: (completeLog: string) => Promise<void>;
  abandonSOPLogging: (completeLog: string) => Promise<void>;
}

// SOP steps extracted from the SYSTEM_MESSAGE in VideoPlayground.tsx
const SOP_STEPS: SOPStep[] = [
  {
    name: "1. Shut down the system",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "2. Unplug power cord",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "3. Discharge static electricity",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "4. Open the chassis",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "5. Locate the DIMM slots",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "6. Identify the module to remove",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "7. Release the retaining clip",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "8. Eject the RAM module",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "9. Remove the module",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "10. Store properly",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "11. Inspect slot & module",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "12. Close the chassis",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "13. Reconnect power",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  },
  {
    name: "14. Boot system (if needed)",
    status: "pending",
    evidence: "Step not yet analyzed",
    message: undefined
  }
];

export const useSOPChecklistStore = create<SOPChecklistState>((set, get) => ({
  checklist: [],
  isValidResponse: false,
  lastResponse: "",
  isInitialized: false,
  currentLogId: null,
  
  initializeSOP: () => {
    set({ 
      checklist: [...SOP_STEPS], 
      isInitialized: true,
      isValidResponse: false,
      lastResponse: ""
    });
  },
  
  addSteps: (steps: SOPStep[]) => {
    set((state) => ({
      checklist: [...state.checklist, ...steps],
    }));
  },
  
  updateStep: (index: number, step: SOPStep) => {
    set((state) => ({
      checklist: state.checklist.map((s, i) => i === index ? step : s),
    }));
  },
  
  updateStepsFromAnalysis: (steps: SOPStep[]) => {
    set((state) => {
      const updatedChecklist = [...state.checklist];
      
      // Update steps based on the analysis results
      // The latest analyze_step call has higher precedence
      steps.forEach((analysisStep) => {
        // Find the matching step in the checklist by name
        const stepIndex = updatedChecklist.findIndex(
          (checklistStep) => checklistStep.name === analysisStep.name
        );
        
        if (stepIndex !== -1) {
          // Update the step with the analysis results
          updatedChecklist[stepIndex] = {
            ...updatedChecklist[stepIndex],
            status: analysisStep.status,
            evidence: analysisStep.evidence,
            message: analysisStep.message
          };
        }
      });
      
      return { checklist: updatedChecklist };
    });
  },
  
  clearChecklist: () => {
    set({ 
      checklist: [], 
      isValidResponse: false, 
      lastResponse: "",
      isInitialized: false,
      currentLogId: null
    });
  },
  
  setLastResponse: (response: string) => {
    set({ lastResponse: response });
  },
  
  setValidResponse: (isValid: boolean) => {
    set({ isValidResponse: isValid });
  },

  startSOPLogging: async (technicianId: string, technicianEmail: string, sopName: string): Promise<string> => {
    const logId = await sopLogger.startSOPLog(technicianId, technicianEmail, sopName);
    set({ currentLogId: logId });
    return logId;
  },

  logStep: async (step: SOPStep) => {
    const { currentLogId } = get();
    if (currentLogId) {
      await sopLogger.updateStep(currentLogId, step);
    }
  },

  completeSOPLogging: async (completeLog: string) => {
    const { currentLogId } = get();
    if (currentLogId) {
      await sopLogger.completeSOPLog(currentLogId, completeLog);
      set({ currentLogId: null });
    }
  },

  abandonSOPLogging: async (completeLog: string) => {
    const { currentLogId } = get();
    if (currentLogId) {
      await sopLogger.abandonSOPLog(currentLogId, completeLog);
      set({ currentLogId: null });
    }
  },
}));

// Helper function to validate JSON response format
export const validateSOPResponse = (response: string): { isValid: boolean; data?: SOPResponse; error?: string } => {
  try {
    // Try to parse the JSON
    const parsed = JSON.parse(response);
    
    // Check if it has the required structure
    if (!parsed.steps || !Array.isArray(parsed.steps)) {
      return { 
        isValid: false, 
        error: "Response must contain a 'steps' array" 
      };
    }
    
    // Validate each step
    for (let i = 0; i < parsed.steps.length; i++) {
      const step = parsed.steps[i];
      if (!step.name || !step.status || !step.evidence) {
        return { 
          isValid: false, 
          error: `Step ${i + 1} is missing required fields (name, status, or evidence)` 
        };
      }
    }
    
    return { isValid: true, data: parsed as SOPResponse };
  } catch (error) {
    return { 
      isValid: false, 
      error: `Invalid JSON format: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
}; 